import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, CircularProgress, Button, useTheme } from '@mui/material';
import { AutoAwesome as PlaybookIcon } from '@mui/icons-material';
import { RootState } from '../../types';
import { fetchRecommendationsForAlert, clearRecommendations } from '../../store/slices/playbooksSlice';
import { PlaybooksService } from '../../services/playbooksService';
import RecommendationCard from './RecommendationCard';

interface PlaybookTabProps {
  alertId: string;
}

const PlaybookTab: React.FC<PlaybookTabProps> = ({ alertId }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { recommendations, selectedSession, isDetailsLoading, error } = useSelector(
    (state: RootState) => state.playbooks,
  );
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchRecommendationsForAlert(alertId) as any);
    return () => { dispatch(clearRecommendations()); };
  }, [alertId]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateError(null);
    try {
      await PlaybooksService.generateForAlert(alertId);
      dispatch(fetchRecommendationsForAlert(alertId) as any);
    } catch (e: any) {
      setGenerateError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  if (isDetailsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="error">{error}</Typography>
      </Box>
    );
  }

  if (!selectedSession) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
        <PlaybookIcon sx={{ fontSize: 40, color: theme.palette.text.tertiary ?? theme.palette.text.disabled }} />
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No playbook generated yet for this alert
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={handleGenerate}
          disabled={generating}
          startIcon={generating ? <CircularProgress size={14} /> : <PlaybookIcon sx={{ fontSize: 16 }} />}
        >
          {generating ? 'Generating…' : 'Generate Playbook'}
        </Button>
        {generateError && (
          <Typography variant="caption" color="error">{generateError}</Typography>
        )}
      </Box>
    );
  }

  if (recommendations.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
        <PlaybookIcon sx={{ fontSize: 40, color: theme.palette.text.tertiary ?? theme.palette.text.disabled }} />
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No IOCs enriched for this alert — nothing to recommend
        </Typography>
        <Button variant="outlined" size="small" onClick={handleGenerate} disabled={generating}>
          {generating ? 'Re-generating…' : 'Re-run Playbook'}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {recommendations.map((rec) => (
        <RecommendationCard key={rec.id} rec={rec} />
      ))}
    </Box>
  );
};

export default PlaybookTab;